import { useLocation, useNavigate, useRevalidator } from '@remix-run/react';
import { Duration, intervalToDuration, isFuture, isPast } from 'date-fns';
import AddToBagImage from 'public/assets/icons/add_to_bag.svg';
import AvailableForImage from 'public/assets/icons/available_for.svg';
import NextDropInImage from 'public/assets/icons/next_drop_in.svg';
import FullSetButtonImage from 'public/assets/icons/full_set_button.svg';
import HoodieButtonImage from 'public/assets/icons/hoodie_button.svg';
import SizeGuideImage from 'public/assets/icons/size_guide.svg';
import SoldOutImage from 'public/assets/icons/sold_out.svg';
import CurrencyIcon from '~/components/CurrencyIcon';
import {
  ComponentPropsWithoutRef,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { productSizings } from '~/common/constants/dictionary';
import { COLOR } from '~/common/constants/product';
import { useProductDetails } from '~/common/hooks/useProductDetails';
import { isBrowser } from '~/common/utils/browser';
import { CartActions, isNumberAlreadyInCart } from '~/common/utils/cart';
import {
  getSizes,
  getVariantId,
  isAvailable,
  isSetProduct,
} from '~/common/utils/product';
import { BackgroundOverlay } from '~/components/backgroundOverlay';
import ColorItems from '~/components/bubbleItems/ColorItems';
import SizeItems from '~/components/bubbleItems/SizeItems';
import SizeGuideDrawer from '~/components/SizeGuideDrawer';
import { Image } from '~/components/wrappers/Image';
import { useCartSSR } from '~/recoil/useCartSSR';
import { FlashSaleStatus, Product, ProductMetafields } from '~/types/product';
import {
  FlashSaleAddToBagNotice,
  FlashSaleProductDescription,
  FlashSaleProductGrid,
  FlashSaleProductImage,
  FlashSaleProductPurchase,
  FlashSaleProductTitle,
  FlashSaleProductTitleMobile,
  FlashSaleTimer,
  FlashSaleTimerWrapper,
  StyledFlashSaleWrapper,
} from './styles';
import { Routes } from '~/common/constants/routes';
import { trackAddToCart } from '~/common/utils/addToCartTracking';
import { evaluateFreeItemRule, FREE_ITEM_MESSAGES } from '~/lib/freeItemRule';
import { FREE_PRICE_LABEL } from '~/common/utils/currency';

interface IProductsList {
  products: Array<Product>;
}

enum ProductTypes {
  hoodie = 'hoodie',
  fullSet = 'full_set',
}

const FlashSaleLayout: FC<IProductsList> = ({ products }) => {
  const [timer, setTimer] = useState<Duration>({});
  const [saleState, setSaleState] = useState(FlashSaleStatus.NextState);
  const [activeTag, setActiveTag] = useState<ProductTypes>(ProductTypes.hoodie);
  const [sizePromptShown, setSizePromptShown] = useState(false);
  const [cartState, setCartState] = useCartSSR();
  const { pathname } = useLocation();
  const revalidator = useRevalidator();
  const navigate = useNavigate();
  const interval = useRef<NodeJS.Timer | null>(null);

  // Find the Hoodie product
  const hoodieProduct = products.find((item) =>
    item.tags.includes(ProductTypes.hoodie),
  );

  // Find the full-set product
  const fullSetProduct = products.find((item) =>
    item.tags.includes(ProductTypes.fullSet),
  );

  // Create a product type object so we can toggle between products
  const productTypes = {
    hoodie: hoodieProduct,
    full_set: fullSetProduct,
  };

  const product = useMemo(() => {
    return productTypes[activeTag];
  }, [activeTag, productTypes]);

  const metafields = useMemo(() => {
    return hoodieProduct?.metafields;
  }, [hoodieProduct]);

  if (!product) {
    return null;
  }

  const {
    isSizingOpen,
    toggleSizingModal,
    setProductSettings,
    productSettings,
    variants,
  } = useProductDetails(product, [product], '');

  const { selectedColor, selectedSize, selectedBottomSize } = productSettings;

  const isSet = isSetProduct(product);

  /**
   * Whether adding this product would break a free-item rule, derived rather than stored.
   *
   * Matches how the PDP does it (`SelectionControls`), and means the shopper is told before
   * tapping rather than after a dead-end tap. A plain const, not `useMemo`: it is a loop
   * over a handful of cart lines, and this file already calls hooks on both sides of the
   * `if (!product)` return above — it does not need another one.
   */
  const freeItemViolation = (() => {
    const price = Number(product.prices.maxPrice);
    if (!Number.isFinite(price) || price !== 0) return null;

    const result = evaluateFreeItemRule([
      ...cartState.items.lineItems.map((item) => ({
        id: item.variantId,
        price: item.price,
        quantity: item.quantity,
      })),
      { id: product.handle, price: 0, quantity: 1 },
    ]);

    return result.ok ? null : result.violation;
  })();

  useEffect(() => {
    const initialColor = product.variants
      .find((item) => item.availableForSale)
      ?.options?.find((item) => item.name === COLOR)?.value;

    if (!initialColor) {
      return;
    }

    // Set initial color without depending on previous state
    setProductSettings({
      ...productSettings,
      selectedColor: initialColor,
    });
  }, [product, activeTag]);

  const sizes =
    getSizes(selectedColor, variants).map((item) => ({
      ...item,
      size: item.size.toUpperCase(),
    })) || [];

  const productSizing = productSizings.find((variant) =>
    variant.type.includes(product.productType),
  );

  const { images, descriptionHtml, title } = product;
  // Let's get the image at position 0, using .at to have an undefined value in case it fails
  const image = images?.at(0);

  const colorName = selectedColor?.split(' - ')[1] || '';

  const isMaxNumberAlreadyInCart =
    isBrowser &&
    isNumberAlreadyInCart(4, product.title, cartState.items?.lineItems);

  const isAvailableForSale = isAvailable(selectedColor, variants);

  const availableToBeAddedToCart =
    isAvailableForSale && !isMaxNumberAlreadyInCart;

  const canBeAddedToCart =
    availableToBeAddedToCart &&
    ![FlashSaleStatus.SaleEnded, FlashSaleStatus.NextDropIn].includes(
      saleState,
    );

  const needsSize = sizes.length > 0 && !selectedSize;

  const handleUpdateCart = () => {
    if (!canBeAddedToCart || saleState === FlashSaleStatus.DropStartTime) {
      return;
    }

    if (needsSize) {
      setSizePromptShown(true);
      return;
    }

    const id = getVariantId(variants, {
      color: selectedColor,
      size: selectedSize,
      bottomSize: isSet ? selectedBottomSize : undefined,
    });

    const newCartState = CartActions.addCartItem(
      cartState.items,
      {
        ...product,
        selectedSize,
        selectedColor,
        selectedBottomSize: isSet ? selectedBottomSize : undefined,
      },
      id,
      pathname,
    );

    // Same object back: the line is already at its stock limit.
    if (newCartState === cartState.items) return;

    // This surface had NO free-item check at all before BPES-237, so a £0.00 product
    // surfaced in the homepage flash sale could be added to an empty bag — a hole the PDP
    // and /cart both covered. Evaluated on the resulting cart (the add is already applied),
    // using the same rule the checkout action enforces.
    const freeItemResult = evaluateFreeItemRule(
      newCartState.lineItems.map((item) => ({
        id: item.variantId,
        price: item.price,
        quantity: item.quantity,
      })),
    );

    // Belt and braces. `freeItemViolation` above already disables the button and shows the
    // reason, so this only catches a cart that changed between render and tap.
    if (!freeItemResult.ok) return;

    setCartState({
      items: newCartState,
    });

    // `product`, not the cart line: this surface renders on the homepage, so the
    // line's `handle` is `/` rather than a product path.
    trackAddToCart({
      product,
      variantId: id,
      cart: newCartState,
      pathname,
      size: selectedSize,
      bottomSize: isSet ? selectedBottomSize : undefined,
    });
  };

  const getMetafieldValue = (metafields: Product['metafields'], key: string) =>
    metafields?.find((item) => item?.key === key)?.value || '';

  const dropStartTime = getMetafieldValue(
    metafields,
    ProductMetafields.DropStartTime,
  );
  const dropEndTime = getMetafieldValue(
    metafields,
    ProductMetafields.DropEndTime,
  );
  const nextDropIn = getMetafieldValue(
    metafields,
    ProductMetafields.NextDropIn,
  );
  const flashSaleColor = getMetafieldValue(
    metafields,
    ProductMetafields.FlashSaleColor,
  );

  useEffect(() => {
    // Only run if we're in the NextState state (ready to determine next state)
    if (saleState !== FlashSaleStatus.NextState) {
      return;
    }

    // Determine the new state based on timestamps
    if (
      isFuture(dropStartTime) &&
      isFuture(dropEndTime) &&
      isFuture(nextDropIn)
    ) {
      setSaleState(FlashSaleStatus.DropStartTime);
    } else if (isPast(dropStartTime) && isFuture(dropEndTime)) {
      setSaleState(FlashSaleStatus.DropEndTime);
    } else if (isPast(dropEndTime) && isFuture(nextDropIn)) {
      setSaleState(FlashSaleStatus.NextDropIn);
    } else if (isPast(nextDropIn)) {
      setSaleState(FlashSaleStatus.SaleEnded);
    }

    revalidator.revalidate();
    // Add proper dependencies to avoid stale values
  }, [saleState, dropStartTime, dropEndTime, nextDropIn]);

  useEffect(() => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }

    // Extract the getDate function outside the interval for better readability
    const getTargetDate = () => {
      switch (saleState) {
        case FlashSaleStatus.DropStartTime:
          return new Date(dropStartTime);
        case FlashSaleStatus.DropEndTime:
          return new Date(dropEndTime);
        case FlashSaleStatus.NextDropIn:
          return new Date(nextDropIn);
        case FlashSaleStatus.SaleEnded:
        default:
          return new Date();
      }
    };

    // Skip setting up timer for sale ended state
    if (saleState === FlashSaleStatus.SaleEnded) {
      revalidator.revalidate();
      return;
    }

    interval.current = setInterval(() => {
      const now = new Date();
      const targetDate = getTargetDate();
      const timeDifference = targetDate.getTime() - now.getTime();

      if (timeDifference <= 0) {
        setSaleState(FlashSaleStatus.NextState);
        clearInterval(interval.current as NodeJS.Timer);
        interval.current = null;
      } else {
        const duration = intervalToDuration({
          start: now,
          end: targetDate,
        });
        setTimer(duration);
      }
    }, 1000);

    // Cleanup function to clear interval when component unmounts or dependencies change
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = null;
      }
    };
  }, [saleState, dropStartTime, dropEndTime, nextDropIn]);

  const getTimerString = () => {
    const toPaddedString = (time?: number) =>
      time?.toString().padStart(2, '0') || '00';
    const hours = toPaddedString(timer?.hours);
    const minutes = toPaddedString(timer?.minutes);
    const seconds = toPaddedString(timer?.seconds);

    return `${hours}:${minutes}:${seconds}`;
  };

  const saleStateTitleComponent = {
    [FlashSaleStatus.DropEndTime]: (
      <Image src={AvailableForImage} height={24} />
    ),
    [FlashSaleStatus.DropStartTime]: (
      <Image src={NextDropInImage} height={24} />
    ),
    [FlashSaleStatus.NextDropIn]: <Image src={NextDropInImage} height={24} />,
    [FlashSaleStatus.SaleEnded]: (
      <Image src={SoldOutImage} width={270} height={37} />
    ),
  };

  return (
    <>
      <SizeGuideDrawer
        toggleSizingModal={toggleSizingModal}
        productSizing={productSizing}
        isSizingOpen={isSizingOpen}
      />

      <StyledFlashSaleWrapper flashSaleColor={flashSaleColor}>
        <FlashSaleTimerWrapper>
          <div>
            <div className="flash-sale-timer-title">
              {isAvailableForSale ? (
                saleStateTitleComponent[
                  saleState as keyof typeof saleStateTitleComponent
                ]
              ) : (
                <Image src={SoldOutImage} width={300} />
              )}
            </div>
            <FlashSaleTimer>{getTimerString()}</FlashSaleTimer>
          </div>
        </FlashSaleTimerWrapper>
        <FlashSaleProductGrid>
          <FlashSaleProductDescription>
            <FlashSaleProductTitle>{title}</FlashSaleProductTitle>
            <div dangerouslySetInnerHTML={{ __html: descriptionHtml }}></div>
          </FlashSaleProductDescription>
          <FlashSaleProductImage>
            <Image src={(image as string) || '#'} />
          </FlashSaleProductImage>
          <FlashSaleProductPurchase>
            <FlashSaleProductTitleMobile>{title}</FlashSaleProductTitleMobile>
            <div className="flash-sale-price">
              <CurrencyIcon
                currencyCode={product.prices.currencyCode}
                variant="white"
              />
              <span>
                {Number(product.prices.maxPrice) === 0
                  ? FREE_PRICE_LABEL
                  : Number(product.prices.maxPrice)?.toFixed(2) || ''}
              </span>
            </div>
            <div className="flash-sale-option">
              <div className="flash-sale-option-title">Option</div>
              <div className="flash-sale-option-content">
                {Object.keys(productTypes).map((tag, index) => {
                  if (!productTypes[tag as keyof typeof productTypes]) {
                    return null;
                  }

                  return (
                    <PillButtons
                      className={
                        tag === activeTag
                          ? 'flash-sale-option-button-active'
                          : ''
                      }
                      onClick={() => {
                        setActiveTag(tag as ProductTypes);
                      }}
                      key={`${tag}_${index}`}
                      buttonType={tag as ProductTypes}
                    />
                  );
                })}
              </div>
            </div>
            <div className="flash-sale-color">
              <div className="flash-sale-color-title">
                Color: {colorName || ''}
              </div>
              <ColorItems
                variants={variants}
                selectedColor={selectedColor}
                selectedSize={selectedSize}
                setProductSettings={setProductSettings}
                isFlashSale
              />
            </div>
            <div className="flash-sale-size">
              <div className="flash-sale-size-header">
                <div className="flash-sale-size-title">Size</div>
                <StyledButton onClick={() => navigate(Routes.Sizing)}>
                  <Image src={SizeGuideImage} width={141} />
                </StyledButton>
              </div>
              <div style={{ minHeight: 40 }}>
                <SizeItems
                  sizes={sizes}
                  setProductSettings={setProductSettings}
                  selectedSize={selectedSize}
                  title={title}
                  showStars
                />
              </div>
            </div>
            {sizePromptShown && needsSize ? (
              <FlashSaleAddToBagNotice role="alert">
                Please select a size
              </FlashSaleAddToBagNotice>
            ) : null}
            {freeItemViolation ? (
              <FlashSaleAddToBagNotice role="status">
                {FREE_ITEM_MESSAGES[freeItemViolation]}
              </FlashSaleAddToBagNotice>
            ) : null}
            <StyledButton
              disabled={
                !canBeAddedToCart ||
                saleState === FlashSaleStatus.DropStartTime ||
                Boolean(freeItemViolation)
              }
              onClick={handleUpdateCart}
              style={{ width: '100%' }}
            >
              <Image
                src={AddToBagImage}
                width={270}
                style={{ width: '270px' }}
              />
            </StyledButton>
          </FlashSaleProductPurchase>
        </FlashSaleProductGrid>
      </StyledFlashSaleWrapper>
      <BackgroundOverlay isOpen={isSizingOpen} onClick={toggleSizingModal} />
    </>
  );
};

export default FlashSaleLayout;

const StyledPillButton = styled.button`
  background: white;
  border-radius: 8px;
  padding: 0 10px;
  height: 32px;
  border-width: 2px;
  border-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  box-shadow:
    0px 0px 4px 0px rgba(0, 0, 0, 0.04),
    0px 8px 16px 0px rgba(0, 0, 0, 0.08);

  &.flash-sale-option-button-active {
    outline: 2px solid #f0a0bf;
    outline-offset: -2px;
  }
`;

const StyledButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;

  :disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  > img {
    max-width: 320px;
    width: 100%;
  }
`;

interface IPillButtons extends ComponentPropsWithoutRef<'button'> {
  buttonType: ProductTypes;
}

export const PillButtons = ({
  children,
  buttonType,
  ...props
}: IPillButtons) => {
  const image = {
    hoodie: <Image src={HoodieButtonImage} width={74} />,
    full_set: <Image src={FullSetButtonImage} width={89} />,
  };

  return (
    <StyledPillButton {...props}>
      {image[buttonType as keyof typeof image]}
    </StyledPillButton>
  );
};
